/* Settings — workspace configuration (Library & admin nav).
   Three tabs: Pipeline (per-stage model assignment), Integrations
   (OpenRouter / Figma / ESP / Email on Acid / Slack), and Team & roles.
   Reads stageDefs + team from window.MAV. All controls are live (local state). */

const { useState: useSetState, useRef: useSetRef, useEffect: useSetEffect } = React;

/* available models, with a 1–4 cost tier (mirrors the new-build tiers) */
const MODELS = [
  { id: 'gemini-3.5-flash',  tier: 1, note: 'Fast · vision' },
  { id: 'deepseek-v4-pro',   tier: 1, note: 'Cheap · reasoning' },
  { id: 'gemini-3.5-pro',    tier: 2, note: 'Balanced · vision' },
  { id: 'perceptron-mk1',    tier: 2, note: 'Visual QA' },
  { id: 'claude-sonnet-4.6', tier: 3, note: 'Strong semantic' },
  { id: 'claude-opus-4.1',   tier: 4, note: 'Max · orchestration' },
];
const ROLES = ['Product Manager', 'Admin · Workspace manager', 'Team lead · Senior', 'Dev · Builder', 'SME · Reviewer'];

function TierDots({ tier }) {
  return <span className="cost-dots">{[0, 1, 2, 3].map(i => <i key={i} className={i < tier ? 'on' : ''}></i>)}</span>;
}

/* generic dropdown reusing the ESP-selector styling */
function Dropdown({ value, options, render, onChange, width }) {
  const [open, setOpen] = useSetState(false);
  const ref = useSetRef(null);
  useSetEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  return (
    <div className="esp-select" ref={ref} style={{ maxWidth: width || 260, width: '100%' }}>
      <button type="button" className={'esp-trigger' + (open ? ' open' : '')} onClick={() => setOpen(o => !o)}>
        <span className="esp-cur">{render ? render(value) : value}</span>
        <I.chevron size={16} style={{ transform: open ? 'rotate(-90deg)' : 'rotate(90deg)' }} />
      </button>
      {open && (
        <div className="esp-menu">
          <div className="esp-options">
            {options.map(o => {
              const val = typeof o === 'string' ? o : o.id;
              return (
                <button key={val} type="button" className={'esp-option' + (val === value ? ' sel' : '')} onClick={() => { onChange(val); setOpen(false); }}>
                  <span style={{ flex: 1 }}>{render ? render(val) : val}</span>
                  {val === value && <I.check size={15} style={{ color: 'var(--pass)' }} />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function SetToggle({ on, onToggle }) {
  return <button type="button" className={'switch' + (on ? ' on' : '')} onClick={onToggle} role="switch" aria-checked={on}><span className="knob"></span></button>;
}

/* ---------- Pipeline tab ---------- */
function PipelineSettings() {
  const { stageDefs } = window.MAV;
  const [assign, setAssign] = useSetState(() => Object.fromEntries(stageDefs.map(s => [s.id, s.model])));
  const [autoEscalate, setAutoEscalate] = useSetState(true);
  const [dirty, setDirty] = useSetState(false);
  const modelById = (id) => MODELS.find(m => m.id === id) || { id, tier: 2, note: '' };
  const set = (sid, model) => { setAssign(a => ({ ...a, [sid]: model })); setDirty(true); };

  const renderModel = (id) => {
    const m = modelById(id);
    return (
      <span className="row gap-8" style={{ alignItems: 'center', minWidth: 0 }}>
        <span className="mono" style={{ fontSize: 12.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>{id}</span>
        <TierDots tier={m.tier} />
      </span>
    );
  };

  return (
    <div className="col gap-20">
      <div className="card card-pad">
        <div className="sec-head">
          <span className="sec-title">Per-stage model assignment</span>
          {dirty && <button className="btn btn-primary" style={{ padding: '7px 14px', fontSize: 12.5 }} onClick={() => setDirty(false)}><I.check size={14} /> Save changes</button>}
        </div>
        <p className="muted" style={{ fontSize: 12.5, margin: '-4px 0 16px', maxWidth: 620 }}>Each leg of the six-stage pipeline runs on its own model. Heavier models lift accuracy; lighter ones save fuel. The cost-tier dots mirror the build-tier spread.</p>
        <div className="stage-assign-head">
          <span>Stage</span><span>Produces</span><span>Model</span>
        </div>
        {stageDefs.map(s => (
          <div key={s.id} className="stage-assign">
            <div className="row gap-11" style={{ minWidth: 0 }}>
              <span className="sa-no">{s.no}</span>
              <span style={{ fontSize: 13.5, fontWeight: 700 }}>{s.name}</span>
            </div>
            <span className="muted mono" style={{ fontSize: 11.5 }}>{s.produces}</span>
            <Dropdown value={assign[s.id]} options={MODELS} render={renderModel} onChange={(m) => set(s.id, m)} width={240} />
          </div>
        ))}
      </div>

      <div className="card card-pad">
        <div className="sec-head"><span className="sec-title">Pipeline behaviour</span></div>
        <div className="pref-row">
          <div><div style={{ fontSize: 13.5, fontWeight: 600 }}>Auto-escalate on low confidence</div><div className="muted" style={{ fontSize: 12 }}>Re-run a stage on the next tier up when QA confidence drops below 70%</div></div>
          <SetToggle on={autoEscalate} onToggle={() => setAutoEscalate(v => !v)} />
        </div>
        <div className="pref-row" style={{ borderBottom: 'none' }}>
          <div><div style={{ fontSize: 13.5, fontWeight: 600 }}>Default build tier for new builds</div><div className="muted" style={{ fontSize: 12 }}>The tier pre-selected in the New build wizard</div></div>
          <Dropdown value="Balanced" options={['Economy', 'Balanced', 'Max quality', 'Wingman']} onChange={() => {}} width={180} />
        </div>
      </div>
    </div>
  );
}

/* ---------- Integrations tab ---------- */
function IntegrationsSettings() {
  const [conn, setConn] = useSetState({ openrouter: true, figma: true, sfmc: true, slack: false });
  const [eoaStatus, setEoaStatus] = useSetState(null);
  const flip = (k) => setConn(c => ({ ...c, [k]: !c[k] }));

  useSetEffect(() => {
    let alive = true;
    const api = window.GooseAPI;
    if (!api || !api.eoaStatus) {
      setEoaStatus({ unavailable: true });
      return () => { alive = false; };
    }
    api.eoaStatus()
      .then(d => { if (alive) setEoaStatus(d && typeof d.configured === 'boolean' ? d : { unavailable: true }); })
      .catch(() => { if (alive) setEoaStatus({ unavailable: true }); });
    return () => { alive = false; };
  }, []);

  const rows = [
    { id: 'openrouter', ico: I.bolt, name: 'OpenRouter', desc: 'Model gateway for every pipeline stage', meta: 'sk-or-•••• 4f2a', accent: 'var(--brand-orange)' },
    { id: 'figma', ico: I.figma, name: 'Figma', desc: 'Pull live frames as a build source via REST', meta: 'token · 2 files', accent: 'var(--running)' },
    { id: 'sfmc', ico: I.code, name: 'Salesforce (SFMC)', desc: 'Primary ESP — CDN upload + AMPscript syntax', meta: 'mavlers.sfmc', accent: 'var(--pass)' },
    { id: 'eoa', ico: I.flame, name: 'Email on Acid', desc: 'Cross-client render diffing for Afterburner QA', accent: 'var(--cond)' },
    { id: 'slack', ico: I.bell, name: 'Slack', desc: 'Post build + review notifications to a channel', meta: '#email-squadron', accent: '#6334E3' },
  ];

  const eoaConfigured = !!(eoaStatus && eoaStatus.configured);
  const connectedCount = Object.values(conn).filter(Boolean).length + (eoaConfigured ? 1 : 0);

  return (
    <div className="card card-pad">
      <div className="sec-head"><span className="sec-title">Connected services</span><span className="muted" style={{ fontSize: 12 }}>{connectedCount} of {rows.length} connected</span></div>
      {rows.map(r => {
        const isEoa = r.id === 'eoa';
        const on = isEoa ? eoaConfigured : conn[r.id];
        return (
          <div key={r.id} className="intg-row">
            <span className="intg-ico" style={{ color: r.accent, background: 'color-mix(in srgb, ' + r.accent + ' 14%, transparent)' }}><r.ico size={18} /></span>
            <div className="grow" style={{ minWidth: 0 }}>
              <div className="row gap-8" style={{ alignItems: 'center' }}>
                <span style={{ fontSize: 13.5, fontWeight: 700 }}>{r.name}</span>
                {on ? <span className="pill pill-pass" style={{ fontSize: 10.5 }}><span className="dot"></span>Connected</span>
                    : isEoa
                      ? <span className="muted" style={{ fontSize: 10.5 }}>{eoaStatus === null
                        ? 'Checking configuration…'
                        : eoaStatus.unavailable
                          ? 'Configuration status unavailable'
                          : 'Not configured — set EOA_API_KEY on the server'}</span>
                      : <span className="chip" style={{ fontSize: 10.5 }}>Not connected</span>}
              </div>
              <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{r.desc}</div>
              {on && <div className="muted mono" style={{ fontSize: 11, marginTop: 4 }}>{isEoa
                ? 'API base · ' + ((eoaStatus && eoaStatus.base) || 'default')
                : r.meta}</div>}
            </div>
            {!isEoa && (on
              ? <button className="btn btn-ghost" style={{ padding: '7px 13px', fontSize: 12.5 }} onClick={() => flip(r.id)}>Disconnect</button>
              : <button className="btn btn-primary" style={{ padding: '7px 14px', fontSize: 12.5 }} onClick={() => flip(r.id)}><I.link size={13} /> Connect</button>)}
          </div>
        );
      })}
    </div>
  );
}

/* ---------- Team & roles tab ---------- */
function TeamSettings() {
  const { team, pods } = window.MAV;
  const [roles, setRoles] = useSetState(() => Object.fromEntries(team.map(m => [m.id, m.role])));
  const [removed, setRemoved] = useSetState({});
  const setRole = (id, r) => setRoles(x => ({ ...x, [id]: r }));
  const active = team.filter(m => !removed[m.id]);

  return (
    <div className="card card-pad">
      <div className="sec-head">
        <span className="sec-title">Members &amp; roles <span className="muted" style={{ fontWeight: 500, fontSize: 12.5 }}>· {active.length} of 12 seats</span></span>
        <button className="btn btn-primary" style={{ padding: '8px 14px', fontSize: 12.5 }}><I.add size={14} /> Invite member</button>
      </div>
      <div className="team-roles-head"><span>Member</span><span>Team</span><span>Role</span><span style={{ textAlign: 'right' }}>Access</span></div>
      {active.map(m => {
        const pod = pods.find(p => p.id === m.pod);
        return (
          <div key={m.id} className="team-role-row">
            <div className="row gap-10" style={{ minWidth: 0 }}>
              <span className="avatar" style={{ width: 32, height: 32, fontSize: 11, background: m.color }}>{m.initials}</span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{m.name}</div>
                <div className="muted mono" style={{ fontSize: 11 }}>{m.id}@mavlers.com</div>
              </div>
            </div>
            <span className="muted" style={{ fontSize: 12 }}>{pod ? pod.name : '—'}</span>
            <Dropdown value={roles[m.id]} options={ROLES} onChange={(r) => setRole(m.id, r)} width={190} />
            <div className="row gap-8" style={{ justifyContent: 'flex-end' }}>
              <button className="icon-act danger" title="Remove from workspace" onClick={() => setRemoved(x => ({ ...x, [m.id]: true }))}><I.cancel size={15} /></button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ============================================================ */
function Settings({ go }) {
  const [tab, setTab] = useSetState('pipeline');
  return (
    <div className="page fade-in">
      <div className="page-head">
        <div><h1 className="page-title">Settings</h1><p className="page-desc">Pipeline models, integrations, and who flies in this workspace.</p></div>
      </div>

      <div className="tabs" style={{ marginBottom: 20 }}>
        <span className={'tab' + (tab === 'pipeline' ? ' active' : '')} onClick={() => setTab('pipeline')}>Pipeline</span>
        <span className={'tab' + (tab === 'integrations' ? ' active' : '')} onClick={() => setTab('integrations')}>Integrations</span>
        <span className={'tab' + (tab === 'team' ? ' active' : '')} onClick={() => setTab('team')}>Team &amp; roles</span>
      </div>

      {tab === 'pipeline' && <PipelineSettings />}
      {tab === 'integrations' && <IntegrationsSettings />}
      {tab === 'team' && <TeamSettings />}
    </div>
  );
}

window.Settings = Settings;
